// ADM-1009 prototype — throwaway page that visualizes the card-ordering audit
// pipeline (Eileen's MVP file summary + Alex's vision of member-level drilldown).
// Runs entirely on hand-curated mock data; no backend, no hooks, no API.
// Not for merge — see prototype/ADM-1009-lens-card-ordering-audit-tracking branch.
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Search,
  Download,
  RefreshCw,
  Clock,
  AlertCircle,
} from 'lucide-react';
import {
  MOCK_CARD_ROWS,
  TENANT_OPTIONS,
  tenantNameFor,
  buildSummaryRows,
} from './cardOrderingAuditMockData';

const PIPELINE_STATUS_STYLES = {
  completed:          { label: 'Completed',           classes: 'bg-green-100 text-green-700' },
  stuck_at_send:      { label: 'Stuck at send',       classes: 'bg-red-100 text-red-800' },
  stuck_at_callback:  { label: 'Stuck at callback',   classes: 'bg-amber-100 text-amber-800' },
  stuck_at_intake:    { label: 'Stuck at intake',     classes: 'bg-amber-100 text-amber-800' },
  not_started:        { label: 'Not started',         classes: 'bg-gray-100 text-gray-700' },
};

function PipelineStatusChip({ value }) {
  const cfg = PIPELINE_STATUS_STYLES[value] || { label: value, classes: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.classes}`}>
      {value === 'completed' && <CheckCircle className="w-3 h-3" />}
      {value !== 'completed' && value !== 'not_started' && <AlertTriangle className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
}

// Color-coded delta badge: green when zero, amber when small (1-2), red when 3+
// or explicitly flagged as an SLA breach. Mirrors the look-and-feel used on the
// File Integrity page so badges feel familiar to existing LENS users.
function DeltaBadge({ value, breached }) {
  if (value == null) return <span className="text-gray-300">-</span>;
  const num = Number(value);
  if (num === 0 && !breached) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" /> 0
      </span>
    );
  }
  const colorClass = breached
    ? 'bg-red-100 text-red-800'
    : 'bg-amber-100 text-amber-800';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
      <AlertTriangle className="w-3 h-3" /> {num > 0 ? '+' : ''}{num}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
}

function downloadCsv(filename, rows, columns) {
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => escape(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => escape(c.value(r))).join(',')).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const CardOrderingAudit = () => {
  const [startDate, setStartDate] = useState('2026-05-04');
  const [endDate, setEndDate] = useState('2026-05-15');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [memberSearch, setMemberSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const cardRows = useMemo(() => {
    let rows = MOCK_CARD_ROWS;
    if (tenantFilter !== 'all') rows = rows.filter((r) => r.tenant_code === tenantFilter);
    rows = rows.filter((r) => {
      if (!r.sent_to_fis_date) return true;
      return r.sent_to_fis_date >= startDate && r.sent_to_fis_date <= endDate;
    });
    return rows;
  }, [tenantFilter, startDate, endDate, refreshKey]);

  const summaryRows = useMemo(() => buildSummaryRows(cardRows), [cardRows]);

  const visibleMemberRows = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return cardRows;
    return cardRows.filter((r) => {
      const name = `${r.first_name} ${r.last_name}`.toLowerCase();
      return (
        name.includes(q) ||
        (r.card_number_proxy || '').toLowerCase().includes(q) ||
        (r.consumer_code || '').toLowerCase().includes(q)
      );
    });
  }, [cardRows, memberSearch]);

  const totals = useMemo(() => {
    const totalCards = cardRows.length;
    const completed = cardRows.filter((r) => r.pipeline_status === 'completed').length;
    const stuck = cardRows.filter((r) => r.pipeline_status.startsWith('stuck_')).length;
    const breached = cardRows.filter((r) => r.sla_breach_total_5_days).length;
    return { totalCards, completed, stuck, breached };
  }, [cardRows]);

  const handleExportCsv = () => {
    downloadCsv(
      `card-ordering-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      visibleMemberRows,
      [
        { label: 'card_number_proxy',     value: (r) => r.card_number_proxy },
        { label: 'consumer_code',         value: (r) => r.consumer_code },
        { label: 'tenant_name',           value: (r) => tenantNameFor(r.tenant_code) },
        { label: 'first_name',            value: (r) => r.first_name },
        { label: 'last_name',             value: (r) => r.last_name },
        { label: 'sent_to_fis_date',      value: (r) => r.sent_to_fis_date },
        { label: 'fis_callback_date',     value: (r) => r.fis_callback_date },
        { label: 'ab_intake_date',        value: (r) => r.ab_intake_date },
        { label: 'ab_shipped_date',       value: (r) => r.ab_shipped_date },
        { label: 'days_send_to_ship',     value: (r) => r.days_send_to_ship },
        { label: 'pipeline_status',       value: (r) => r.pipeline_status },
        { label: 'sla_breach_total_5_days', value: (r) => r.sla_breach_total_5_days },
      ]
    );
  };

  return (
    <div className="space-y-6">
      {/* Prototype banner — makes the throwaway nature unmistakable */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-amber-900">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>Prototype:</strong> ADM-1009 walkthrough only. This page renders 100% mock data
          and is not wired to the smoke-test endpoint.
        </span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Card Ordering Audit</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track the LENS &rarr; FIS &rarr; AB Corp card-issuance pipeline; surface stuck cards before members notice.
          </p>
        </div>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Tenant</label>
            <select
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Tenants</option>
              {TENANT_OPTIONS.map((t) => (
                <option key={t.code} value={t.code}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard label="Cards in window"  value={totals.totalCards}                  icon={<Clock className="w-4 h-4" />} />
        <KpiCard label="Completed"        value={totals.completed}        tone="ok"  icon={<CheckCircle className="w-4 h-4" />} />
        <KpiCard label="Currently stuck"  value={totals.stuck}            tone="warn" icon={<AlertTriangle className="w-4 h-4" />} />
        <KpiCard label="SLA breaches (>5 days)" value={totals.breached}   tone="bad"  icon={<AlertCircle className="w-4 h-4" />} />
      </div>

      {/* Section 1: File / Date Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">File / Date Summary</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Per-day, per-tenant rollup of stage-gate counts (Eileen's MVP view).
            </p>
          </div>
          <span className="text-sm text-gray-500">{summaryRows.length} rows</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Date</Th>
                <Th>Tenant</Th>
                <Th align="right">Sent to FIS</Th>
                <Th align="right">FIS callback</Th>
                <Th align="right">AB intake</Th>
                <Th align="right">AB shipped</Th>
                <Th align="center">Send &rarr; Callback delta</Th>
                <Th align="center">Callback &rarr; Ship delta</Th>
                <Th align="center">SLA breaches</Th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No rows in the selected window.
                  </td>
                </tr>
              ) : (
                summaryRows.map((r) => (
                  <tr key={`${r.date}|${r.tenant_code}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <Td>{formatDate(r.date)}</Td>
                    <Td>{r.tenant_name}</Td>
                    <Td align="right" className="tabular-nums font-semibold">{r.sent_count}</Td>
                    <Td align="right" className="tabular-nums">{r.callback_count}</Td>
                    <Td align="right" className="tabular-nums">{r.intake_count}</Td>
                    <Td align="right" className="tabular-nums">{r.shipped_count}</Td>
                    <Td align="center"><DeltaBadge value={r.delta_send_to_callback} /></Td>
                    <Td align="center"><DeltaBadge value={r.delta_callback_to_ship} /></Td>
                    <Td align="center">
                      {r.breached_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertTriangle className="w-3 h-3" /> {r.breached_count}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3" /> 0
                        </span>
                      )}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2: Member / Card Drilldown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Member / Card Drilldown</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              One row per card; status chip shows where it sits in the pipeline today.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search name / proxy / consumer ID"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-72"
              />
            </div>
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <span className="text-sm text-gray-500">{visibleMemberRows.length} cards</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Proxy</Th>
                <Th>Member</Th>
                <Th>Tenant</Th>
                <Th>Sent to FIS</Th>
                <Th>FIS callback</Th>
                <Th>AB intake</Th>
                <Th>AB shipped</Th>
                <Th align="right">Days total</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {visibleMemberRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No cards match the current filters / search.
                  </td>
                </tr>
              ) : (
                visibleMemberRows.map((r) => (
                  <tr key={r.card_number_proxy} className={`border-b border-gray-100 hover:bg-gray-50 ${r.sla_breach_total_5_days ? 'bg-red-50/60' : ''}`}>
                    <Td className="font-mono text-xs">{r.card_number_proxy}</Td>
                    <Td>
                      <div className="font-medium text-gray-900">{r.first_name} {r.last_name}</div>
                      <div className="text-xs text-gray-500 font-mono truncate max-w-[14rem]" title={r.consumer_code}>{r.consumer_code}</div>
                    </Td>
                    <Td className="text-sm">{tenantNameFor(r.tenant_code)}</Td>
                    <Td className="text-sm">{formatDate(r.sent_to_fis_date)}</Td>
                    <Td className="text-sm">{formatDate(r.fis_callback_date)}</Td>
                    <Td className="text-sm">{formatDate(r.ab_intake_date)}</Td>
                    <Td className="text-sm">{formatDate(r.ab_shipped_date)}</Td>
                    <Td align="right" className="tabular-nums">
                      {r.days_send_to_ship == null ? '-' : (
                        <span className={r.sla_breach_total_5_days ? 'text-red-700 font-semibold' : ''}>
                          {r.days_send_to_ship}
                        </span>
                      )}
                    </Td>
                    <Td><PipelineStatusChip value={r.pipeline_status} /></Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function Th({ children, align = 'left' }) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${alignClass}`}>
      {children}
    </th>
  );
}

function Td({ children, align = 'left', className = '' }) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return <td className={`px-4 py-2.5 ${alignClass} ${className}`}>{children}</td>;
}

function KpiCard({ label, value, icon, tone }) {
  const toneClasses =
    tone === 'ok'   ? 'text-green-700 bg-green-50  border-green-200'  :
    tone === 'warn' ? 'text-amber-800 bg-amber-50  border-amber-200'  :
    tone === 'bad'  ? 'text-red-800   bg-red-50    border-red-200'    :
                      'text-gray-700  bg-white     border-gray-200';
  return (
    <div className={`rounded-xl border p-4 ${toneClasses}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      <div className="text-3xl font-bold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

export default CardOrderingAudit;
